import React, { useEffect, useState, useCallback, useRef } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import {
  Paper,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { GetApp } from "@material-ui/icons";
import MUIDataTable from "mui-datatables";
import { MuiThemeProvider } from "@material-ui/core";

import { actionTableMuiTheme } from "../table/table_styles";
import { setLoader } from "../../../redux/ducks/load.duck";
import {
  getWeeklyReport,
  exportWeeklyReportCSV,
} from "../../crud/api/cs_reporting.crud";
import notice from "../../utils/notice";

const VIEW_TYPES = [
  { value: "team", label: "Raw Data - Weekly Usage" },
  { value: "partner", label: "Blaze Partner Raw" },
];

const teamColumns = [
  { name: "partner_name", label: "Partner", options: { filter: false, sort: false } },
  { name: "team_name", label: "Team", options: { filter: false, sort: false } },
  { name: "week_period", label: "Week", options: { filter: false, sort: false } },
  { name: "total_athletes", label: "Total Athletes", options: { filter: false, sort: false } },
  { name: "activated_athletes", label: "Activated", options: { filter: false, sort: false } },
  {
    name: "activation_rate",
    label: "Activation %",
    options: {
      filter: false,
      sort: false,
      customBodyRender: (value) => `${value}%`,
    },
  },
  { name: "weekly_active_athletes", label: "Weekly Active", options: { filter: false, sort: false } },
  { name: "checkin_completions", label: "Check-ins", options: { filter: false, sort: false } },
  { name: "content_interactions", label: "Content", options: { filter: false, sort: false } },
  { name: "sessions_initiated", label: "Sessions", options: { filter: false, sort: false } },
  { name: "sessions_completed", label: "Completed", options: { filter: false, sort: false } },
  { name: "high_concern_checkins", label: "High Concern", options: { filter: false, sort: false } },
  {
    name: "participation_rate_percent",
    label: "Participation %",
    options: {
      filter: false,
      sort: false,
      customBodyRender: (value) => `${value}%`,
    },
  },
];

const partnerColumns = [
  { name: "partner_name", label: "Partner", options: { filter: false, sort: false } },
  { name: "week_period", label: "Week", options: { filter: false, sort: false } },
  { name: "teams_active_count", label: "Teams Active", options: { filter: false, sort: false } },
  { name: "teams_total_count", label: "Teams Total", options: { filter: false, sort: false } },
  {
    name: "team_coverage_rate",
    label: "Coverage %",
    options: {
      filter: false,
      sort: false,
      customBodyRender: (value) => `${value}%`,
    },
  },
  { name: "total_athletes", label: "Athletes", options: { filter: false, sort: false } },
  { name: "activated_athletes", label: "Activated", options: { filter: false, sort: false } },
  {
    name: "activation_rate",
    label: "Activation %",
    options: {
      filter: false,
      sort: false,
      customBodyRender: (value) => `${value}%`,
    },
  },
  { name: "weekly_active_athletes", label: "Weekly Active", options: { filter: false, sort: false } },
  { name: "checkin_completions", label: "Check-ins", options: { filter: false, sort: false } },
  { name: "content_interactions", label: "Content", options: { filter: false, sort: false } },
  { name: "weekly_active_staff_total", label: "Active Staff", options: { filter: false, sort: false } },
  { name: "dashboard_views_total", label: "Dash Views", options: { filter: false, sort: false } },
  { name: "escalations_triggered_total", label: "Escalations", options: { filter: false, sort: false } },
  { name: "high_concern_checkins", label: "High Concern", options: { filter: false, sort: false } },
  {
    name: "high_concern_percentage_total",
    label: "Concern %",
    options: {
      filter: false,
      sort: false,
      customBodyRender: (value) => `${value}%`,
    },
  },
];

const addDaysUTC = (dateStr, days) => {
  const parts = dateStr.split("-");
  const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().split("T")[0];
};

const CSReporting = ({ settingLoader }) => {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [filters, setFilters] = useState({ partners: [], weeks: [] });
  const [viewType, setViewType] = useState("team");
  const [selectedWeek, setSelectedWeek] = useState("");
  const [selectedPartner, setSelectedPartner] = useState("");
  const [csvLoading, setCsvLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const initializedRef = useRef(false);

  const buildParams = useCallback(
    (page = 1) => ({
      view_type: viewType,
      page,
      per_page: 25,
      week_start: selectedWeek || undefined,
      week_end: selectedWeek ? addDaysUTC(selectedWeek, 6) : undefined,
      partner_id: selectedPartner || undefined,
    }),
    [viewType, selectedWeek, selectedPartner]
  );

  const fetchData = useCallback(
    async (page = 1) => {
      setLoading(true);
      settingLoader(true);
      try {
        const res = await getWeeklyReport(buildParams(page));
        setData({
          items: res.items || [],
          total: res.total || 0,
          page: res.page || 1,
          pages: res.pages || 0,
        });
        if (res.filters) {
          setFilters(res.filters);
        }
        return res;
      } catch (e) {
        console.error("Failed to fetch report:", e);
      } finally {
        setLoading(false);
        settingLoader(false);
      }
    },
    [buildParams, settingLoader]
  );

  // Initial load: fetch data and set default week
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    fetchData().then((res) => {
      if (res?.filters?.weeks?.length > 0) {
        setSelectedWeek(res.filters.weeks[0].week_start);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when filters change (after initialization)
  useEffect(() => {
    if (!initializedRef.current) return;
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    setCsvLoading(true);
    try {
      await exportWeeklyReportCSV(buildParams());
    } catch {
      // notice already shown by exportWeeklyReportCSV
    } finally {
      setCsvLoading(false);
    }
  };

  const columns = viewType === "partner" ? partnerColumns : teamColumns;

  const options = {
    serverSide: true,
    rowsPerPageOptions: [],
    rowsPerPage: 25,
    page: data.page - 1,
    count: data.total,
    search: false,
    sort: false,
    print: false,
    filter: false,
    viewColumns: false,
    download: false,
    rowHover: false,
    selectableRows: "none",
    textLabels: {
      body: {
        noMatch: loading ? "Loading..." : "No data available for this period",
      },
    },
    onChangePage: (newPage) => fetchData(newPage + 1),
  };

  return (
    <div className="table-container">
      <h3 className="mb-4">Weekly CS Reporting</h3>

      <Paper className="mb-3 p-3">
        <Tabs
          value={viewType}
          onChange={(e, val) => setViewType(val)}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          {VIEW_TYPES.map((vt) => (
            <Tab key={vt.value} value={vt.value} label={vt.label} />
          ))}
        </Tabs>
      </Paper>

      <div className="d-flex justify-content-between align-items-end mb-4">
        <div className="d-flex" style={{ gap: 16 }}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel>Week</InputLabel>
            <Select
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value)}
              label="Week"
            >
              <MenuItem value="">All Weeks</MenuItem>
              {filters.weeks.map((w) => (
                <MenuItem key={w.week_start} value={w.week_start}>
                  {w.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel>Partner</InputLabel>
            <Select
              value={selectedPartner}
              onChange={(e) => setSelectedPartner(e.target.value)}
              label="Partner"
            >
              <MenuItem value="">All Partners</MenuItem>
              {filters.partners.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <Button
          variant="contained"
          color="default"
          onClick={handleExport}
          startIcon={
            csvLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <GetApp />
            )
          }
          disabled={csvLoading}
        >
          {csvLoading ? "Exporting..." : "Download CSV"}
        </Button>
      </div>

      <MuiThemeProvider theme={actionTableMuiTheme()}>
        <MUIDataTable columns={columns} data={data.items} options={options} />
      </MuiThemeProvider>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  settingLoader: bindActionCreators(setLoader, dispatch),
});

export default connect(null, mapDispatchToProps)(CSReporting);
